import { BookingInitialState } from "stores/booking/types";

// Define an interface for a BookingStore object
interface BookingStore {
  isTravelProtected: boolean;
  totalAmt: number | null;
  discount: number | null;
  achProcessingSavings?: number | null;
}

// Calculate the amount to be paid
export function calculateAmount(bookingStore: Partial<BookingStore>, travelProtectionCost: number, pdpFees: number, rate: number): number {
  const discount = bookingStore?.discount || 0; // Get the discount amount from the booking store object
  const total = bookingStore.totalAmt || 0;
  const travelProtection = bookingStore.isTravelProtected ? travelProtectionCost : 0;
  const achProcessingSavings = bookingStore?.achProcessingSavings || 0;
  const amount = total + pdpFees + travelProtection - discount - achProcessingSavings;
  return amount <= 0 ? 0 : amount * rate; // Return the amount adjusted by the rate
}
interface PaymentModeFeeParams {
  isAffirmPayment?: boolean;
  // Affirm fee only applies on the payment step (PriceDetails guards this with `step === 2`)
  applyAffirmFee?: boolean;
  affirmFeePercentage?: number;
  isKlarnaPayment?: boolean;
  klarnaFeePercentage?: number;
  klarnaFixedFee?: number;
  totalIncludingTaxes?: number;
  totalAmt?: number;
  pdpFees?: number;
}

// Calculate the extra fee charged for deferred payment modes (Affirm / Klarna).
// Returns the raw (pre-currency-rate) fee so callers can add it to their own totals.
// This is the single source of truth shared by PriceDetails and BookingPriceHeader
// so the header total and the price breakdown can't drift apart.
export function calculatePaymentModeFee({
  isAffirmPayment = false,
  applyAffirmFee = false,
  affirmFeePercentage = 0,
  isKlarnaPayment = false,
  klarnaFeePercentage = 0,
  klarnaFixedFee = 0,
  totalIncludingTaxes = 0,
  totalAmt = 0,
  pdpFees = 0
}: PaymentModeFeeParams): number {
  const affirmFee = applyAffirmFee && isAffirmPayment ? ((totalIncludingTaxes || 0) + (pdpFees || 0)) * (affirmFeePercentage || 0) / 100 : 0;
  const klarnaFee = isKlarnaPayment ? ((totalIncludingTaxes || totalAmt) + (pdpFees || 0)) * (klarnaFeePercentage || 0) / 100 + (klarnaFixedFee || 0) : 0;
  return affirmFee + klarnaFee;
}

// Function to calculate half of the total payment
export function calculateOfFirstPayment(rate: number, bookingStore: BookingInitialState, hostFees: number, totalTax: number, serviceFee: number, travelProtectionCost: number, firstPaymentPercentage: number): number {
  const isTravelProtected = bookingStore.isTravelProtected || false;
  const isCancelBookingTravelProtected = bookingStore.isCancelBookingTravelProtected || false;
  const getOriginalServiceFee = bookingStore?.prequote?.originalServiceFee || 0;

  // Calculate the travel protection cost
  const insurance = isTravelProtected && !isCancelBookingTravelProtected ? travelProtectionCost : isCancelBookingTravelProtected ? travelProtectionCost : 0;
  const discount = bookingStore.discount || 0;
  // Calculate the original rent with the given rate
  const originalRent = bookingStore.prequote?.originalRent || 0;

  // Calculate the percentage of the original service fee
  const percentage = getOriginalServiceFee === 0 ? firstPaymentPercentage : 50;
  const achProcessingSavings = bookingStore?.achProcessingSavings || 0;
  // Calculate half of the total amount with taxes,fees and discount
  const countHalfTotalWithTaxesAndDiscount = (originalRent + hostFees + totalTax - discount - achProcessingSavings) * percentage / 100;
  // Calculate the full total with service fee and travel protection cost
  const countFullTotalWithFeesAndInsurance = serviceFee + insurance;
  // the final amount adjusted by the rate
  return (countHalfTotalWithTaxesAndDiscount + countFullTotalWithFeesAndInsurance) * rate;
}